import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { parseFormSubmissionCSV, validateFormSubmission } from "@/utils/formSubmissionParser";
import { ParsedFormSubmission } from "@/types/formSubmissions";
import { Upload, CheckCircle2, AlertCircle, FileText } from "lucide-react";
import { recordImportMetadata } from "@/utils/importMetadata";

type EntityType = 'restaurant' | 'operator' | 'consolidator';

export function FormSubmissionImport() {
  const { isAdmin, isEditor } = useUserRole();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [entityType, setEntityType] = useState<EntityType>('restaurant');
  const [parsedData, setParsedData] = useState<ParsedFormSubmission[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  // Only admin and editor can access
  if (!isAdmin && !isEditor) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Você não tem permissão para importar dados de formulários.
        </AlertDescription>
      </Alert>
    );
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast({
          title: "Erro",
          description: "Por favor, selecione um arquivo CSV.",
          variant: "destructive",
        });
        return;
      }
      setFile(selectedFile);
      setParsedData([]);
    }
  };

  const handleProcessFile = async () => {
    if (!file) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um arquivo CSV.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Fetch existing entities to match against
      const { data: entities, error } = await supabase
        .from('entities')
        .select('id, type, name, state, region')
        .eq('type', entityType);

      if (error) throw error;

      const parsed = await parseFormSubmissionCSV(
        file,
        entityType,
        entities || []
      );

      setParsedData(parsed);
      
      toast({
        title: "Arquivo processado",
        description: `${parsed.length} registros encontrados. Revise os dados antes de importar.`,
      });
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: "Erro ao processar arquivo",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (parsedData.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum dado para importar.",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const submission of parsedData) {
        const validationErrors = validateFormSubmission(submission);
        if (validationErrors.length > 0) {
          console.error('Validation errors for submission:', submission, validationErrors);
          errorCount++;
          continue;
        }

        const { error } = await supabase
          .from('form_submissions')
          .insert({
            entity_id: submission.matchedEntityId,
            entity_type: submission.entity_type,
            company_name: submission.company_name,
            state: submission.state,
            region: submission.region,
            form_link: submission.form_link,
            logo_link: submission.logo_link,
            validation_status: submission.hasConflict ? 'pending' : 'pending',
            raw_data: submission.raw_data,
          });

        if (error) {
          console.error('Error inserting submission:', error);
          errorCount++;
        } else {
          successCount++;
        }
      }

      // Record metadata
      await recordImportMetadata({
        tableName: 'form_submissions',
        entityType,
        updateType: 'csv_import',
        recordsAffected: successCount,
      });

      toast({
        title: "Importação concluída",
        description: `${successCount} registros importados com sucesso. ${errorCount} erros.`,
      });

      // Reset state
      setFile(null);
      setParsedData([]);
    } catch (error) {
      console.error('Error importing data:', error);
      toast({
        title: "Erro na importação",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const conflictCount = parsedData.filter(d => d.hasConflict).length;
  const matchedCount = parsedData.filter(d => d.matchedEntityId).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Importar Formulários do Google Forms</CardTitle>
        <CardDescription>
          Faça upload do CSV exportado do Google Forms para importar dados de formulários e logos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="entity-type">Tipo de Entidade</Label>
          <Select value={entityType} onValueChange={(value) => setEntityType(value as EntityType)}>
            <SelectTrigger id="entity-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="restaurant">Restaurante</SelectItem>
              <SelectItem value="operator">Operadora</SelectItem>
              <SelectItem value="consolidator">Consolidadora</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="csv-file">Arquivo CSV</Label>
          <div className="flex gap-2">
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
            />
            <Button
              onClick={handleProcessFile}
              disabled={!file || isProcessing}
            >
              {isProcessing ? "Processando..." : "Processar"}
            </Button>
          </div>
        </div>

        {parsedData.length > 0 && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Total
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{parsedData.length}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Correspondidos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{matchedCount}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    Conflitos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-yellow-600">{conflictCount}</div>
                </CardContent>
              </Card>
            </div>

            {conflictCount > 0 && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Existem {conflictCount} registros com conflitos ou não correspondidos. 
                  Eles serão importados mas precisarão de validação manual.
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleImport}
              disabled={isImporting}
              className="w-full"
            >
              <Upload className="mr-2 h-4 w-4" />
              {isImporting ? "Importando..." : `Importar ${parsedData.length} registros`}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
